// Speaking indicators only. This graph never connects to a speaker, recorder
// or transport and never takes ownership of the meeting's capture tracks.
export const createVoiceActivityState = ({
  energyThreshold = 0.12,
  speakHold = 80,
  dropHold = 240,
} = {}) => {
  let speaking = false;
  let onset = null;
  let lastVoice = null;
  let noiseFloor = 0.002;
  const reset = () => {
    speaking = false;
    onset = null;
    lastVoice = null;
    noiseFloor = 0.002;
    return { level: 0, isSpeaking: false };
  };
  return {
    reset,
    sample(rms, now, available = true) {
      if (!available || !Number.isFinite(rms)) return reset();
      const amplitude = Math.max(0, rms);
      // Time-domain RMS avoids diluting a quiet voice across every frequency
      // bin. Adapt only below the gate so sustained speech is not learned away.
      const ceiling = Math.max(0.008, Number(energyThreshold) / 6 || 0.02);
      const threshold = Math.max(0.006, Math.min(ceiling, noiseFloor * 3.2 + 0.005));
      if (amplitude >= threshold) {
        if (onset === null) onset = now;
        lastVoice = now;
        if (now - onset >= Math.max(0, Number(speakHold) || 0)) speaking = true;
      } else {
        if (!speaking) {
          onset = null;
          noiseFloor = Math.min(ceiling / 2, noiseFloor * 0.96 + amplitude * 0.04);
        }
        if (speaking && now - lastVoice >= Math.max(0, Number(dropHold) || 0)) {
          speaking = false;
          onset = null;
        }
      }
      return { level: Math.min(1, amplitude * 6), isSpeaking: speaking };
    },
  };
};

export const audioTimeDomainRms = (samples) => {
  if (!samples?.length) return 0;
  let mean = 0;
  for (const value of samples) mean += value;
  mean /= samples.length;
  let energy = 0;
  for (const value of samples) energy += (value - mean) ** 2;
  return Math.sqrt(energy / samples.length);
};

export const createVoiceActivityMonitor = ({
  stream,
  context,
  onUpdate,
  fftSize = 512,
  smoothingTimeConstant = 0.25,
  minDb = -80,
  maxDb = -10,
  sampleIntervalMs = 50,
  target = window,
  documentTarget = document,
  MediaStreamClass = MediaStream,
  ...activityOptions
}) => {
  const activity = createVoiceActivityState(activityOptions);
  let stopped = false;
  let source = null;
  let analyser = null;
  let track = null;
  let samples = null;
  let bytes = null;
  let last = { level: -1, isSpeaking: false };
  let resumePending = false;
  const report = (value) => {
    if (stopped) return;
    if (value.isSpeaking !== last.isSpeaking ||
        Math.abs(value.level - last.level) >= 0.02 ||
        (value.level === 0 && last.level !== 0)) {
      last = value;
      onUpdate(value);
    }
  };
  const clear = () => report(activity.reset());
  const resume = () => {
    if (stopped || context.state === 'running' || context.state === 'closed' || resumePending) return;
    resumePending = true;
    Promise.resolve().then(() => context.resume?.()).catch(() => undefined)
      .finally(() => { resumePending = false; });
  };
  const detach = () => {
    track?.removeEventListener?.('ended', rebind);
    track?.removeEventListener?.('mute', clear);
    track?.removeEventListener?.('unmute', resume);
    try { source?.disconnect(); } catch {}
    try { analyser?.disconnect(); } catch {}
    source = null;
    analyser = null;
    track = null;
    samples = null;
    bytes = null;
  };
  function rebind() {
    if (stopped) return;
    const current = stream?.getAudioTracks?.().find((value) => value.readyState === 'live') || null;
    if (current === track && analyser) return;
    detach();
    clear();
    if (!current || context.state === 'closed') return;
    try {
      source = context.createMediaStreamSource(new MediaStreamClass([current]));
      analyser = context.createAnalyser();
      analyser.fftSize = fftSize;
      analyser.smoothingTimeConstant = smoothingTimeConstant;
      analyser.minDecibels = minDb;
      analyser.maxDecibels = maxDb;
      source.connect(analyser);
      track = current;
      samples = new Float32Array(analyser.fftSize);
      bytes = new Uint8Array(analyser.fftSize);
      track.addEventListener?.('ended', rebind);
      track.addEventListener?.('mute', clear);
      track.addEventListener?.('unmute', resume);
      resume();
    } catch {
      detach();
      clear();
    }
  }
  const sample = () => {
    if (stopped) return;
    rebind();
    if (!track || !analyser || track.enabled === false || track.muted === true || context.state !== 'running') {
      clear();
      return;
    }
    try {
      if (analyser.getFloatTimeDomainData) analyser.getFloatTimeDomainData(samples);
      else {
        analyser.getByteTimeDomainData(bytes);
        for (let index = 0; index < bytes.length; index += 1) samples[index] = (bytes[index] - 128) / 128;
      }
      report(activity.sample(audioTimeDomainRms(samples), target.performance?.now?.() ?? Date.now()));
    } catch { clear(); }
  };
  const restore = () => {
    if (documentTarget?.visibilityState === 'hidden') return;
    rebind();
    resume();
    sample();
  };
  const contextState = () => {
    if (context.state !== 'running') clear();
    if (context.state === 'interrupted') resume();
  };
  stream?.addEventListener?.('addtrack', rebind);
  stream?.addEventListener?.('removetrack', rebind);
  target.addEventListener?.('pointerdown', resume, { passive: true });
  target.addEventListener?.('keydown', resume);
  target.addEventListener?.('focus', restore);
  target.addEventListener?.('pageshow', restore);
  documentTarget?.addEventListener?.('visibilitychange', restore);
  context.addEventListener?.('statechange', contextState);
  const timer = target.setInterval(sample, Math.max(30, Number(sampleIntervalMs) || 50));
  rebind();
  sample();
  return () => {
    stopped = true;
    target.clearInterval(timer);
    stream?.removeEventListener?.('addtrack', rebind);
    stream?.removeEventListener?.('removetrack', rebind);
    target.removeEventListener?.('pointerdown', resume);
    target.removeEventListener?.('keydown', resume);
    target.removeEventListener?.('focus', restore);
    target.removeEventListener?.('pageshow', restore);
    documentTarget?.removeEventListener?.('visibilitychange', restore);
    context.removeEventListener?.('statechange', contextState);
    detach();
  };
};
